import React, { useEffect, useMemo, useRef } from "react";
import { useTable, usePagination } from "react-table";
import { getCellAlignProps } from "../../../../utils/helper";

// QR Logs (generated QR batches) list with client-side pagination and per-row Excel download.
// "Show Records" select lives in the search row (Paginated header), so page size comes in as a prop.
const TemplateTable = ({ data, onDownload, pageSize: recordsPerPage = 10 }) => {
    // Keep latest handler without recreating columns (which would reset the table)
    const onDownloadRef = useRef(onDownload);
    onDownloadRef.current = onDownload;

    const columns = useMemo(() => [
        {
            Header: "Product Description",
            accessor: "productdescription",
        },
        {
            Header: "Quantity",
            accessor: "quantity",
        },
        {
            Header: "Value",
            accessor: "value",
        },
        {
            Header: "Batch No.",
            accessor: "batchno",
        },
        {
            Header: "Download",
            id: "download",
            Cell: ({ row }) => (
                <button
                    type="button"
                    className="btn btn-primary btn-sm d-flex align-items-center"
                    onClick={() => onDownloadRef.current(row.original.templateCode, "")}
                >
                    <span>Download</span>
                    <i className="fa-solid fa-file-excel ms-2"></i>
                </button>
            ),
        },
    ], []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        pageOptions,
        gotoPage,
        nextPage,
        previousPage,
        canNextPage,
        canPreviousPage,
        setPageSize,
        state: { pageIndex },
    } = useTable(
        {
            columns,
            data,
            initialState: { pageSize: recordsPerPage },
        },
        usePagination
    );

    useEffect(() => {
        setPageSize(recordsPerPage);
    }, [recordsPerPage, setPageSize]);

    return (
        <>
            <div className="customTable__body">
                <div className="table-responsive">
                    <table className="table table-striped table-bordered sticky_table" {...getTableProps()}>
                        <thead>
                            {headerGroups.map((headerGroup) => (
                                <tr {...headerGroup.getHeaderGroupProps()}>
                                    {headerGroup.headers.map((column) => (
                                        <th {...column.getHeaderProps()}>{column.render("Header")}</th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {page.length ? (
                                page.map((row) => {
                                    prepareRow(row);
                                    return (
                                        <tr {...row.getRowProps()}>
                                            {row.cells.map((cell) => (
                                                <td {...getCellAlignProps(cell)}>{cell.render("Cell")}</td>
                                            ))}
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={columns.length} style={{ textAlign: "center", display: "table-cell", textTransform: "Capitalize" }}>
                                        No data found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="customTable__footer">
                <div className="customTable__left">
                    <div className="customTable__totalTransactions">
                        <p>
                            Total Records: <span>{data?.length || 0}</span>
                        </p>
                    </div>
                </div>
                <div className="customTable__right">
                    <div className="customTable__top">
                        <div className="customTable__totalPages">
                            <p>
                                Page
                                <select
                                    className="form-control selectArrow"
                                    value={pageIndex + 1}
                                    onChange={(e) => gotoPage(Number(e.target.value) - 1)}
                                >
                                    {(pageOptions.length ? pageOptions : [0]).map((index) => (
                                        <option key={index} value={index + 1}>
                                            {index + 1}
                                        </option>
                                    ))}
                                </select>
                                of<span>{pageOptions.length || 1}</span>
                            </p>
                        </div>
                    </div>
                    <div className="customTable__bottom">
                        <div className="customTable__buttons">
                            <button className="btn btn-outline-primary" onClick={() => previousPage()} disabled={!canPreviousPage}>
                                {"Previous"}
                            </button>
                            <button className="btn btn-outline-primary" onClick={() => nextPage()} disabled={!canNextPage}>
                                {"Next"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TemplateTable;
